import React from 'react';
import { useNavigate } from 'react-router-dom';

function TermsOfUse() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="mb-8 flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Home
        </button>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Use</h1>
          <p className="text-gray-600">Last Updated: January 25, 2025</p>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing or using ZuperBooks ("Service"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree to these Terms, do not use the Service.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>ZuperBooks, LLC</strong><br />
              A Michigan Limited Liability Company<br />
              Headquarters: Michigan, United States
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">
              ZuperBooks provides a cloud-based inventory and financial management platform for DTC (Direct-to-Consumer) brands. The Service includes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Real-time inventory tracking and management</li>
              <li>Financial analytics and COGS calculation</li>
              <li>AI-powered recommendations and alerts</li>
              <li>Multi-channel integration capabilities</li>
              <li>User management and role-based access control</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Free Trial Terms</h2>
            <p className="text-gray-700 mb-4">
              <strong>90-Day Free Trial:</strong> New users receive 90 days of free access to ZuperBooks with full functionality.
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>No credit card required to start the trial</li>
              <li>Full access to all features during the trial period</li>
              <li>Cancel anytime without charges</li>
              <li>Extension requests will be considered on a case-by-case basis</li>
            </ul>
            <p className="text-gray-700 mb-4">
              After the 90-day trial, you may quote your own price based on your usage. We reserve the right to accept or decline the quoted price.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Accounts</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Account Creation</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>You must provide accurate and complete information</li>
              <li>You are responsible for maintaining account security</li>
              <li>You must be authorized to represent your business</li>
              <li>One account per business entity</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Account Responsibilities</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>You are responsible for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Do not share login credentials</li>
              <li>Maintain confidentiality of sensitive business data</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Acceptable Use</h2>
            <p className="text-gray-700 mb-4">You agree NOT to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Upload malicious code or viruses</li>
              <li>Attempt to gain unauthorized access to systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use the Service for illegal activities</li>
              <li>Resell or redistribute the Service without authorization</li>
              <li>Use automated systems to scrape data</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Our IP</h3>
            <p className="text-gray-700 mb-4">
              All content, features, and functionality of ZuperBooks, including but not limited to text, graphics, logos, software, and design, are owned by ZuperBooks, LLC and protected by copyright, trademark, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Your Data</h3>
            <p className="text-gray-700 mb-4">
              You retain all rights to your business data uploaded to the Service. By using the Service, you grant us a limited license to use, store, and process your data solely to provide the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Payment Terms (Post-Trial)</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>After the 90-day trial, you may submit a price quote based on your usage</li>
              <li>Pricing is negotiated on a case-by-case basis</li>
              <li>We reserve the right to accept or decline any pricing proposal</li>
              <li>Payment terms will be specified in your individual agreement</li>
              <li>All fees are non-refundable unless otherwise stated</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Termination</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.1 By You</h3>
            <p className="text-gray-700 mb-4">
              You may terminate your account at any time by contacting support@zuperbooks.com. Upon termination, your data will be retained for 90 days unless you request immediate deletion.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.2 By Us</h3>
            <p className="text-gray-700 mb-4">
              We may suspend or terminate your access if you violate these Terms or engage in fraudulent activity. We will provide notice when reasonably possible.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Disclaimers and Limitations of Liability</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">9.1 Service "As Is"</h3>
            <p className="text-gray-700 mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">9.2 Limitation of Liability</h3>
            <p className="text-gray-700 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZUPERBOOKS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES.
            </p>
            <p className="text-gray-700 mb-4">
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Indemnification</h2>
            <p className="text-gray-700 mb-4">
              You agree to indemnify and hold harmless ZuperBooks, its affiliates, and their respective officers, directors, employees, and agents from any claims, losses, damages, liabilities, and expenses arising out of your use of the Service or violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law and Jurisdiction</h2>
            <p className="text-gray-700 mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the State of Michigan, United States, without regard to its conflict of law provisions.
            </p>
            <p className="text-gray-700 mb-4">
              Any disputes arising from these Terms or the Service shall be resolved in the state or federal courts located in Michigan, and you consent to the exclusive jurisdiction of such courts.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right to modify these Terms at any time. We will notify you of material changes via email or through the Service. Continued use of the Service after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Miscellaneous</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and ZuperBooks</li>
              <li><strong>Severability:</strong> If any provision is found invalid, the remaining provisions remain in effect</li>
              <li><strong>Waiver:</strong> Failure to enforce any right does not waive that right</li>
              <li><strong>Assignment:</strong> You may not assign these Terms without our consent</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms, contact us:
            </p>
            <div className="bg-gray-100 p-6 rounded-lg">
              <p className="text-gray-700"><strong>ZuperBooks, LLC</strong></p>
              <p className="text-gray-700">Headquarters: Michigan, United States</p>
              <p className="text-gray-700">Email: legal@zuperbooks.com</p>
              <p className="text-gray-700">Phone: 1-800-ZUPER-BK</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default TermsOfUse;
